import type { CropRectangle, VideoSample } from "mediabunny";

export type CropRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type PresentationSample = {
  ordinal: number;
  timestamp: number;
  microsecondTimestamp: number;
};

export type VideoInspection = {
  displayHeight: number;
  displayWidth: number;
  duration: number;
  fileFingerprint: string;
  rotation: 0 | 90 | 180 | 270;
  samples: PresentationSample[];
};

export type TrimResult = {
  blob: Blob;
  duration: number;
  frameCount: number;
  framesPerSecond: number;
  height: number;
  mimeType: string;
  startTime: number;
  url: string;
  width: number;
};

type ExportAlignedClipOptions = {
  cropRect: CropRect;
  file: File;
  inspection: VideoInspection;
  onProgress?: (progress: number) => void;
  selection: PresentationSample;
};

type CropCanvasRenderer = {
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
  displayHeight: number;
  displayWidth: number;
};

const frameCount = 31;
const framesPerSecond = 30;
const anchorFrameIndex = 15;
const microsecondsPerSecond = 1_000_000;
const anchorContextMicroseconds = 500_000;
const outputDuration = frameCount / framesPerSecond;
const maximumImpactRgbMeanAbsoluteError = 8;
const timestampToleranceMicroseconds = 1;

let mediabunnyPromise: Promise<typeof import("mediabunny")> | null = null;

export const alignedClipContract = {
  anchorFrameIndex,
  frameCount,
  framesPerSecond,
  outputDuration,
  preAnchorDuration: anchorFrameIndex / framesPerSecond,
  postAnchorDuration: (frameCount - 1 - anchorFrameIndex) / framesPerSecond,
} as const;

export async function inspectVideoFile(file: File): Promise<VideoInspection> {
  const media = await loadMediabunny();
  const input = new media.Input({
    formats: media.ALL_FORMATS,
    source: new media.BlobSource(file),
  });

  try {
    const track = await input.getPrimaryVideoTrack();
    if (!track) {
      throw new AlignedClipError("视频中没有可读取的画面轨道");
    }
    if (!(await track.canDecode())) {
      throw new AlignedClipError("当前浏览器无法解码这个视频格式，请更换浏览器或视频");
    }

    const [duration, displayWidth, displayHeight, rotation] = await Promise.all([
      input.computeDuration(),
      track.getDisplayWidth(),
      track.getDisplayHeight(),
      track.getRotation(),
    ]);

    const sink = new media.VideoSampleSink(track);
    const samples: PresentationSample[] = [];
    let previousTimestamp = Number.NEGATIVE_INFINITY;

    for await (const sample of sink.samples()) {
      try {
        const timestamp = sample.timestamp;
        const microsecondTimestamp = sample.microsecondTimestamp;
        if (!Number.isFinite(timestamp) || !Number.isSafeInteger(microsecondTimestamp)) {
          throw new AlignedClipError("视频包含无效的帧时间信息");
        }
        if (microsecondTimestamp <= previousTimestamp) {
          throw new AlignedClipError("视频帧时间顺序异常，请更换视频");
        }
        samples.push({
          microsecondTimestamp,
          ordinal: samples.length,
          timestamp,
        });
        previousTimestamp = microsecondTimestamp;
      } finally {
        sample.close();
      }
    }

    if (samples.length === 0) {
      throw new AlignedClipError("无法读取视频的精确帧时间线");
    }
    if (!Number.isFinite(duration) || duration <= 0) {
      throw new AlignedClipError("视频时长信息无效");
    }
    if (!Number.isFinite(displayWidth) || !Number.isFinite(displayHeight) || displayWidth < 2 || displayHeight < 2) {
      throw new AlignedClipError("视频画面尺寸无效");
    }

    return {
      displayHeight: Math.round(displayHeight),
      displayWidth: Math.round(displayWidth),
      duration,
      fileFingerprint: fingerprint(file),
      rotation,
      samples,
    };
  } catch (error) {
    throw normalizeInspectionError(error);
  } finally {
    input.dispose();
  }
}

export function nearestPresentationSample(
  samples: PresentationSample[],
  targetSeconds: number,
): PresentationSample | null {
  if (samples.length === 0 || !Number.isFinite(targetSeconds)) return null;
  const target = Math.round(targetSeconds * microsecondsPerSecond);
  let lowerBound = 0;
  let upperBound = samples.length;

  while (lowerBound < upperBound) {
    const midpoint = Math.floor((lowerBound + upperBound) / 2);
    if (samples[midpoint].microsecondTimestamp < target) {
      lowerBound = midpoint + 1;
    } else {
      upperBound = midpoint;
    }
  }

  if (lowerBound === 0) return samples[0];
  if (lowerBound === samples.length) return samples[samples.length - 1];

  const earlier = samples[lowerBound - 1];
  const later = samples[lowerBound];
  return target - earlier.microsecondTimestamp <= later.microsecondTimestamp - target ? earlier : later;
}

export function validAnchorSamples(inspection: VideoInspection): PresentationSample[] {
  const first = inspection.samples[0];
  const last = inspection.samples[inspection.samples.length - 1];
  if (!first || !last) return [];

  return inspection.samples.filter((sample) => (
    sample.microsecondTimestamp - anchorContextMicroseconds >= first.microsecondTimestamp
    && sample.microsecondTimestamp + anchorContextMicroseconds <= last.microsecondTimestamp
  ));
}

export function isValidAnchorSelection(
  inspection: VideoInspection,
  selection: PresentationSample | null,
): boolean {
  if (!selection) return false;
  const current = inspection.samples[selection.ordinal];
  if (!current || current.microsecondTimestamp !== selection.microsecondTimestamp) return false;

  const first = inspection.samples[0];
  const last = inspection.samples[inspection.samples.length - 1];
  return Boolean(
    first
    && last
    && selection.microsecondTimestamp - anchorContextMicroseconds >= first.microsecondTimestamp
    && selection.microsecondTimestamp + anchorContextMicroseconds <= last.microsecondTimestamp,
  );
}

export async function exportAlignedClip({
  cropRect,
  file,
  inspection,
  onProgress,
  selection,
}: ExportAlignedClipOptions): Promise<TrimResult> {
  assertCurrentFile(file, inspection);
  if (!isValidAnchorSelection(inspection, selection)) {
    throw new AlignedClipError("击球时刻前后必须各保留至少 0.5 秒画面");
  }

  const media = await loadMediabunny();
  if (!(await media.canEncodeVideo("avc"))) {
    throw new AlignedClipError("当前浏览器无法编码 AI 教练需要的 H.264 MP4，请更换浏览器或设备");
  }

  const mapping = makeFrameMapping(inspection, selection);
  const resolvedCrop = resolveCropRect(cropRect, inspection.displayWidth, inspection.displayHeight);
  const averageBitRate = Math.min(50_000_000, Math.max(4_000_000, resolvedCrop.width * resolvedCrop.height * 8));
  const input = new media.Input({
    formats: media.ALL_FORMATS,
    source: new media.BlobSource(file),
  });
  const target = new media.BufferTarget();
  const output = new media.Output({
    format: new media.Mp4OutputFormat({ fastStart: "in-memory" }),
    target,
  });
  let outputStarted = false;

  try {
    const track = await input.getPrimaryVideoTrack();
    if (!track || !(await track.canDecode())) {
      throw new AlignedClipError("当前浏览器无法再次解码这个视频，请重新选择视频");
    }

    const source = new media.VideoSampleSource({
      bitrate: averageBitRate,
      codec: "avc",
      keyFrameInterval: outputDuration,
      sizeChangeBehavior: "deny",
    });
    output.addVideoTrack(source, {
      frameRate: framesPerSecond,
      maximumPacketCount: frameCount,
      rotation: 0,
    });
    await output.start();
    outputStarted = true;

    const sink = new media.VideoSampleSink(track);
    const requestedTimestamps = mapping.map((entry) => entry.source.timestamp);
    const cropRenderer = createCropCanvasRenderer(
      inspection.displayWidth,
      inspection.displayHeight,
      resolvedCrop,
    );
    let outputIndex = 0;
    let expectedImpactRgb: Uint8ClampedArray | null = null;

    for await (const decodedSample of sink.samplesAtTimestamps(requestedTimestamps)) {
      if (!decodedSample) {
        throw new AlignedClipError("选定时间附近存在无法解码的视频帧");
      }

      const mapped = mapping[outputIndex];
      if (!mapped || Math.abs(decodedSample.microsecondTimestamp - mapped.source.microsecondTimestamp) > timestampToleranceMicroseconds) {
        decodedSample.close();
        throw new AlignedClipError("视频帧定位结果不稳定，请重新选择视频");
      }

      let transformed: VideoSample | null = null;
      try {
        renderCroppedFrame(decodedSample, resolvedCrop, cropRenderer);
        transformed = new media.VideoSample(cropRenderer.canvas, {
          duration: 1 / framesPerSecond,
          rotation: 0,
          timestamp: outputIndex / framesPerSecond,
        });

        if (outputIndex === anchorFrameIndex) {
          expectedImpactRgb = cropRenderer.context.getImageData(
            0,
            0,
            cropRenderer.canvas.width,
            cropRenderer.canvas.height,
          ).data;
        }

        await source.add(transformed, { keyFrame: outputIndex === 0 });
      } finally {
        transformed?.close();
        decodedSample.close();
      }

      outputIndex += 1;
      onProgress?.(Math.round((outputIndex / frameCount) * 100));
    }

    if (outputIndex !== frameCount || !expectedImpactRgb) {
      throw new AlignedClipError(`导出帧数异常：需要 ${frameCount} 帧，实际 ${outputIndex} 帧`);
    }

    await output.finalize();
    const buffer = target.buffer;
    if (!buffer || buffer.byteLength === 0) {
      throw new AlignedClipError("导出结果为空");
    }

    const blob = new Blob([buffer], { type: "video/mp4" });
    await validateAlignedOutput(blob, resolvedCrop, expectedImpactRgb);

    return {
      blob,
      duration: outputDuration,
      frameCount,
      framesPerSecond,
      height: resolvedCrop.height,
      mimeType: "video/mp4",
      startTime: selection.timestamp - alignedClipContract.preAnchorDuration,
      url: URL.createObjectURL(blob),
      width: resolvedCrop.width,
    };
  } catch (error) {
    if (outputStarted && output.state !== "finalized" && output.state !== "canceled") {
      await output.cancel().catch(() => undefined);
    }
    throw normalizeExportError(error);
  } finally {
    input.dispose();
  }
}

function createCropCanvasRenderer(
  displayWidth: number,
  displayHeight: number,
  crop: CropRectangle,
): CropCanvasRenderer {
  const canvas = document.createElement("canvas");
  canvas.width = crop.width;
  canvas.height = crop.height;
  const context = canvas.getContext("2d", { willReadFrequently: true });

  if (!context) {
    throw new AlignedClipError("当前浏览器无法创建视频裁剪画布");
  }

  return {
    canvas,
    context,
    displayHeight,
    displayWidth,
  };
}

function renderCroppedFrame(
  sample: VideoSample,
  crop: CropRectangle,
  renderer: CropCanvasRenderer,
): void {
  const { canvas, context, displayHeight, displayWidth } = renderer;

  if (
    Math.round(sample.displayWidth) !== displayWidth
    || Math.round(sample.displayHeight) !== displayHeight
  ) {
    throw new AlignedClipError("视频帧画面尺寸发生变化，请更换视频");
  }

  resetCanvas(context, canvas);
  context.save();
  context.translate(-crop.left, -crop.top);
  switch (sample.rotation) {
    case 90:
      context.translate(displayWidth, 0);
      context.rotate(Math.PI / 2);
      break;
    case 180:
      context.translate(displayWidth, displayHeight);
      context.rotate(Math.PI);
      break;
    case 270:
      context.translate(0, displayHeight);
      context.rotate(-Math.PI / 2);
      break;
    default:
      break;
  }

  // Safari can ignore the source rectangle of drawImage for VideoFrame and
  // squeeze the full frame into the crop-sized destination. Draw the complete
  // oriented frame and let the crop-sized canvas clip the selected area.
  context.drawImage(
    sample.toCanvasImageSource(),
    0,
    0,
    sample.squarePixelWidth,
    sample.squarePixelHeight,
  );
  context.restore();
}

function resetCanvas(
  context: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
): void {
  context.save();
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.globalCompositeOperation = "copy";
  context.fillStyle = "#000";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.restore();
}

function makeFrameMapping(
  inspection: VideoInspection,
  selection: PresentationSample,
): Array<{ outputIndex: number; source: PresentationSample }> {
  const mapping: Array<{ outputIndex: number; source: PresentationSample }> = [];

  for (let outputIndex = 0; outputIndex < frameCount; outputIndex += 1) {
    const offsetMicroseconds = Math.round(
      ((outputIndex - anchorFrameIndex) / framesPerSecond) * microsecondsPerSecond,
    );
    const targetSeconds = (selection.microsecondTimestamp + offsetMicroseconds) / microsecondsPerSecond;
    const source = outputIndex === anchorFrameIndex
      ? selection
      : nearestPresentationSample(inspection.samples, targetSeconds);
    if (!source) {
      throw new AlignedClipError("无法为输出片段匹配完整帧序列");
    }
    mapping.push({ outputIndex, source });
  }

  for (let index = 1; index < mapping.length; index += 1) {
    if (mapping[index - 1].source.ordinal > mapping[index].source.ordinal) {
      throw new AlignedClipError("视频帧时间顺序异常，请更换视频");
    }
  }
  if (mapping[anchorFrameIndex].source.ordinal !== selection.ordinal) {
    throw new AlignedClipError("击球帧选择已经失效，请重新选择");
  }
  return mapping;
}

function resolveCropRect(
  normalizedCropRect: CropRect,
  pixelWidth: number,
  pixelHeight: number,
): CropRectangle {
  const evenRightLimit = Math.floor(pixelWidth) - Math.floor(pixelWidth) % 2;
  const evenBottomLimit = Math.floor(pixelHeight) - Math.floor(pixelHeight) % 2;
  if (evenRightLimit < 2 || evenBottomLimit < 2) {
    throw new AlignedClipError("视频画面尺寸不足，无法裁剪");
  }

  const values = [
    normalizedCropRect.x,
    normalizedCropRect.y,
    normalizedCropRect.width,
    normalizedCropRect.height,
  ];
  if (
    values.some((value) => !Number.isFinite(value))
    || normalizedCropRect.x < 0
    || normalizedCropRect.y < 0
    || normalizedCropRect.width <= 0
    || normalizedCropRect.height <= 0
    || normalizedCropRect.x + normalizedCropRect.width > 1.000001
    || normalizedCropRect.y + normalizedCropRect.height > 1.000001
  ) {
    throw new AlignedClipError("框选区域无效，请重新框选分析对象");
  }

  const left = Math.max(0, evenFloor(normalizedCropRect.x * pixelWidth));
  const top = Math.max(0, evenFloor(normalizedCropRect.y * pixelHeight));
  const right = Math.min(evenRightLimit, evenCeil((normalizedCropRect.x + normalizedCropRect.width) * pixelWidth));
  const bottom = Math.min(evenBottomLimit, evenCeil((normalizedCropRect.y + normalizedCropRect.height) * pixelHeight));
  if (right <= left || bottom <= top) {
    throw new AlignedClipError("框选区域无效，请重新框选分析对象");
  }

  return {
    height: bottom - top,
    left,
    top,
    width: right - left,
  };
}

async function validateAlignedOutput(
  blob: Blob,
  crop: CropRectangle,
  expectedImpactRgb: Uint8ClampedArray,
): Promise<void> {
  const media = await loadMediabunny();
  const input = new media.Input({
    formats: media.ALL_FORMATS,
    source: new media.BlobSource(blob),
  });

  try {
    const [videoTracks, audioTracks, duration] = await Promise.all([
      input.getVideoTracks(),
      input.getAudioTracks(),
      input.computeDuration(),
    ]);
    if (videoTracks.length !== 1 || audioTracks.length !== 0) {
      throw new AlignedClipError("导出视频轨道结构不符合 AI 教练要求");
    }

    const track = videoTracks[0];
    const [codec, rotation, width, height] = await Promise.all([
      track.getCodec(),
      track.getRotation(),
      track.getDisplayWidth(),
      track.getDisplayHeight(),
    ]);
    if (codec !== "avc") {
      throw new AlignedClipError("导出视频不是 H.264 编码");
    }
    if (rotation !== 0 || width !== crop.width || height !== crop.height) {
      throw new AlignedClipError("导出视频的旋转或画面尺寸校验失败");
    }
    if (Math.abs(duration - outputDuration) * microsecondsPerSecond > timestampToleranceMicroseconds) {
      throw new AlignedClipError("导出视频时长校验失败");
    }

    const sink = new media.VideoSampleSink(track);
    let decodedFrameCount = 0;
    let actualImpactRgb: Uint8ClampedArray | null = null;
    for await (const sample of sink.samples()) {
      try {
        if (decodedFrameCount >= frameCount) {
          throw new AlignedClipError("导出视频包含多余帧");
        }
        const expectedTimestamp = decodedFrameCount / framesPerSecond;
        if (Math.abs(sample.timestamp - expectedTimestamp) * microsecondsPerSecond > timestampToleranceMicroseconds) {
          throw new AlignedClipError(`导出视频第 ${decodedFrameCount + 1} 帧的时间戳校验失败`);
        }
        if (decodedFrameCount === anchorFrameIndex) {
          actualImpactRgb = renderRgb(sample);
        }
        decodedFrameCount += 1;
      } finally {
        sample.close();
      }
    }

    if (decodedFrameCount !== frameCount || !actualImpactRgb) {
      throw new AlignedClipError(`导出视频必须恰好包含 ${frameCount} 帧`);
    }
    const impactError = rgbMeanAbsoluteError(expectedImpactRgb, actualImpactRgb);
    if (impactError > maximumImpactRgbMeanAbsoluteError) {
      throw new AlignedClipError(`击球帧画面校验失败（误差 ${impactError.toFixed(2)}）`);
    }
  } finally {
    input.dispose();
  }
}

function renderRgb(sample: VideoSample): Uint8ClampedArray {
  const canvas = document.createElement("canvas");
  canvas.width = sample.displayWidth;
  canvas.height = sample.displayHeight;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) {
    throw new AlignedClipError("无法验证导出视频画面");
  }
  sample.draw(context, 0, 0, canvas.width, canvas.height);
  return context.getImageData(0, 0, canvas.width, canvas.height).data;
}

function rgbMeanAbsoluteError(expected: Uint8ClampedArray, actual: Uint8ClampedArray): number {
  if (expected.length !== actual.length || expected.length === 0) {
    throw new AlignedClipError("击球帧画面尺寸校验失败");
  }
  let total = 0;
  let count = 0;
  for (let index = 0; index < expected.length; index += 4) {
    total += Math.abs(expected[index] - actual[index]);
    total += Math.abs(expected[index + 1] - actual[index + 1]);
    total += Math.abs(expected[index + 2] - actual[index + 2]);
    count += 3;
  }
  return total / count;
}

function assertCurrentFile(file: File, inspection: VideoInspection): void {
  if (fingerprint(file) !== inspection.fileFingerprint) {
    throw new AlignedClipError("视频文件已变化，请重新选择视频");
  }
  if (inspection.samples.length === 0) {
    throw new AlignedClipError("视频帧时间线已经失效，请重新选择视频");
  }
}

function fingerprint(file: File): string {
  return `${file.name}\u0000${file.size}\u0000${file.lastModified}\u0000${file.type}`;
}

function evenFloor(value: number): number {
  return Math.floor(value / 2) * 2;
}

function evenCeil(value: number): number {
  return Math.ceil(value / 2) * 2;
}

async function loadMediabunny(): Promise<typeof import("mediabunny")> {
  if (!window.isSecureContext) {
    throw new AlignedClipError("视频精确处理需要 HTTPS 或本机 localhost 环境");
  }
  if (!("VideoDecoder" in window) || !("VideoEncoder" in window)) {
    throw new AlignedClipError("当前浏览器缺少精确视频处理能力，请更新浏览器或更换设备");
  }
  mediabunnyPromise ??= import("mediabunny");
  return mediabunnyPromise;
}

function normalizeInspectionError(error: unknown): Error {
  if (error instanceof AlignedClipError) return error;
  return new AlignedClipError("无法读取视频的精确帧信息，请更换浏览器或视频");
}

function normalizeExportError(error: unknown): Error {
  if (error instanceof AlignedClipError) return error;
  return new AlignedClipError("没有成功生成符合 AI 教练要求的视频，请更换浏览器或视频后重试");
}

class AlignedClipError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AlignedClipError";
  }
}
